import type { ObjectId } from '@racemap/sdk/schema/base';
import { type UserOverview, UserOverviewSchema } from '@racemap/sdk/schema/user';
import { type DateFilter, RacemapAPIClient } from '@racemap/utilities/api-client';
import type { RacemapEvent } from '@racemap/utilities/types/events';
import { produce } from 'immer';
import type { Immutable } from 'immer';
import type { StoreApi } from 'zustand';
import { type DraftState, type State, useStore } from './reducers';

const apiClient = RacemapAPIClient.fromWindowLocation();

export enum SearchByOptions {
  DATE = 'DATE',
  USER = 'USER',
}

export interface LoadDataParams {
  searchBy: SearchByOptions;
  dateFilter: Array<DateFilter>;
  toDate: Date;
  fromDate: Date;
  userIdToSearchFor: ObjectId | null;
}

export interface ManageEventsState {
  manageEvents: {
    events: Map<string, RacemapEvent>;
    users: Map<string, UserOverview>;
    isLoading: boolean;

    actions: {
      _loadEvents: (params: LoadDataParams) => Promise<void>;
      _loadUsers: (options: { userIds: Array<ObjectId> }) => Promise<void>;
      loadData: (params: LoadDataParams) => Promise<void>;
    };
  };
}

export const createManageEventsStore = (
  set: StoreApi<State>['setState'],
  get: StoreApi<State>['getState'],
): Immutable<ManageEventsState> => ({
  manageEvents: {
    events: new Map(),
    users: new Map(),
    isLoading: false,
    actions: {
      _loadEvents: async ({ searchBy, dateFilter, toDate, fromDate, userIdToSearchFor }) => {
        let events: Array<RacemapEvent>;

        if (searchBy === SearchByOptions.DATE) {
          if (dateFilter.length === 0) {
            set(
              produce((s: DraftState) => {
                s.manageEvents.events = new Map();
              }),
            );
            return;
          }

          events = await apiClient.getEvents({
            dateFilter: [...dateFilter],
            toDate: toDate,
            fromDate: fromDate,
            show: ['hidden'],
            filter: 'can-edit',
            attachRenewedAt: 'true',
          });
        } else {
          if (userIdToSearchFor == null) {
            set(
              produce((s: DraftState) => {
                s.manageEvents.events = new Map();
              }),
            );
            return;
          }

          console.log({ userIdToSearchFor });

          events = await apiClient.getEvents({
            show: ['hidden'],
            attachRenewedAt: 'true',
            findByCreator: userIdToSearchFor.toHexString(),
            findByEditor: userIdToSearchFor.toHexString(),
          });
        }

        const userIdsOfEvents = new Set<string>();
        events.forEach((event) => {
          if (event.creatorId != null) {
            userIdsOfEvents.add(event.creatorId);
          }
          if (event.editorIds != null) {
            event.editorIds.forEach((editorId) => {
              userIdsOfEvents.add(editorId);
            });
          }
        });
        // hotfix: user ids become to large
        // const userIds = Array.from(userIdsOfEvents).map((id) => new ObjectId(id));
        // await get().manageEvents.actions._loadUsers({ userIds });

        set(
          produce((s: DraftState) => {
            s.manageEvents.events = new Map(events.map((e) => [e.id, e]));
          }),
        );
      },
      _loadUsers: async ({ userIds }) => {
        const { users } = await apiClient.getUsers({ withEventStats: true, ids: userIds });

        set(
          produce((s: DraftState) => {
            s.manageEvents.users = new Map(users.map((u) => [u.id, UserOverviewSchema.parse(u)]));
          }),
        );
      },
      loadData: async (params) => {
        try {
          set(
            produce((s: DraftState) => {
              s.manageEvents.isLoading = true;
            }),
          );

          const state = get().manageEvents;
          await state.actions._loadEvents(params);
        } finally {
          set(
            produce((s: DraftState) => {
              s.manageEvents.isLoading = false;
            }),
          );
        }
      },
    },
  },
});

export const useIsLoading = () => useStore((s) => s.manageEvents.isLoading);
export const useManageEventsActions = () => useStore((s) => s.manageEvents.actions);
